import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { EditorialReviewEntities } from 'client/data/models/editorial-review';
import { getValidImageUrl, SOURCES } from 'client/utils/image-helpers';
import { AuthorTooltip } from 'site-modules/shared/components/editorial/author-tooltip/author-tooltip';
import { ArticleDateline } from 'site-modules/shared/components/editorial/article-metadata/article-dateline';

import './article-author.scss';

function wrapAuthorLink({ link, name, about, title, showBioTooltip, byline, tooltipProps, showTitle }) {
  return (
    <Fragment>
      {byline}{' '}
      {link ? (
        <a
          className="author btn-link text-gray-darker text-capitalize align-baseline text-nowrap text-underline fw-bold"
          href={link}
          data-tracking-id="view_author_page"
          rel="nofollow"
        >
          {name}
        </a>
      ) : (
        <span itemProp="author" className="author text-gray-darker align-baseline">
          {name}
        </span>
      )}
      {showBioTooltip && <AuthorTooltip aboutAuthor={about} name={name} title={title} {...tooltipProps} />}
      {showTitle && <div>Edmunds {title.replace(/^edmunds\s/i, '')}</div>}
    </Fragment>
  );
}

export function ArticleAuthor({
  author = null,
  ratedBy = null,
  byline = 'by',
  tooltipProps = null,
  dateModified = null,
  publishDate = null,
  updatedLabel = null,
  className = 'align-items-center size-12 mb-1_5 mt-1',
  photoClassName = 'rounded me-0_75',
  imageHeight = 45,
  imageWidth = 45,
  lazyLoadImage = false,
  showBioTooltip = false,
  showTitle = false,
  showPhoto = true,
  isDateTextBelow = false,
}) {
  const { authorName, photoPath, authorLink, aboutAuthor, authorTitle } = author || {};
  const {
    authorName: ratedByName,
    photoPath: ratedByPhotoPath,
    authorLink: ratedByLink,
    aboutAuthor: aboutRatedBy,
    authorTitle: ratedByTitle,
  } = ratedBy || {};
  const imageLoading = lazyLoadImage ? 'lazy' : undefined;
  const imageProps = {
    className: classnames('author-photo', photoClassName),
    'aria-hidden': 'true',
    height: imageHeight,
    width: imageWidth,
    loading: imageLoading,
    style: { objectFit: 'cover' },
  };

  const dateText = !!publishDate && (
    <div>
      {!isDateTextBelow && <span className="mx-0_5">•</span>}
      <ArticleDateline
        updated={dateModified}
        published={publishDate}
        textClassName="medium"
        updatedLabel={updatedLabel}
      />
    </div>
  );

  return (
    <div className={classnames('article-author d-flex text-gray-darker', className)}>
      {authorName ? (
        <Fragment>
          {showPhoto && photoPath && <img src={getValidImageUrl(photoPath, SOURCES.STATIC)} alt="" {...imageProps} />}
          {showPhoto && ratedByPhotoPath && (
            <img src={getValidImageUrl(ratedByPhotoPath, SOURCES.STATIC)} alt="" {...imageProps} />
          )}
          <div className={classnames('author-links', { 'both-authors': !!ratedByName })}>
            {wrapAuthorLink({
              link: authorLink,
              name: authorName,
              about: aboutAuthor,
              title: authorTitle,
              showBioTooltip,
              byline,
              tooltipProps,
              showTitle,
            })}
            {ratedByName && (
              <div>
                {wrapAuthorLink({
                  link: ratedByLink,
                  name: ratedByName,
                  about: aboutRatedBy,
                  title: ratedByTitle,
                  byline: 'rating written by',
                  showBioTooltip,
                })}
              </div>
            )}
            {isDateTextBelow && dateText}
          </div>
        </Fragment>
      ) : (
        <div>
          by <span className="author fw-bold">the Edmunds Experts</span>
          {isDateTextBelow && dateText}
        </div>
      )}
      {!isDateTextBelow && dateText}
    </div>
  );
}

ArticleAuthor.propTypes = {
  author: EditorialReviewEntities.AuthorData,
  ratedBy: EditorialReviewEntities.AuthorData,
  byline: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  tooltipProps: PropTypes.shape({
    suffix: PropTypes.node,
    trackingSubaction: PropTypes.string,
    trackingParent: PropTypes.string,
  }),
  dateModified: PropTypes.string,
  publishDate: PropTypes.string,
  updatedLabel: PropTypes.string,
  imageHeight: PropTypes.number,
  imageWidth: PropTypes.number,
  className: PropTypes.string,
  photoClassName: PropTypes.string,
  lazyLoadImage: PropTypes.bool,
  showBioTooltip: PropTypes.bool,
  showTitle: PropTypes.bool,
  showPhoto: PropTypes.bool,
  isDateTextBelow: PropTypes.bool,
};
